/**
 * Create-a-passkey "don't show again" preference.
 *
 * The one-time create-passkey prompt persists a single flag so the offer never
 * nags once the user opts out. Kept in its own module, separate from the prompt
 * component, so a non-component export does not sit in a .tsx file and both the
 * prompt (which writes it) and the App gate (which reads it) share one seam.
 */

/**
 * localStorage key persisting the user's "don't show this again" choice for the
 * create-passkey offer. Versioned like the store's other persisted keys so the
 * meaning can change in a later build without an old value being misread.
 */
export const CREATE_PASSKEY_DISMISSED_KEY = 'pmw.createPasskeyDismissed.v1';

/**
 * Whether the user asked never to see the create-passkey offer again. Reads the
 * persisted flag, tolerating a blocked or unavailable localStorage by treating it
 * as not dismissed, so the gate fails toward offering the passkey rather than
 * silently suppressing it.
 *
 * @returns True when the offer should stay hidden.
 */
export function isCreatePasskeyDismissed(): boolean {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(CREATE_PASSKEY_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

/** Persist the "don't show this again" choice, tolerating a storage failure. */
export function persistCreatePasskeyDismissed(): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(CREATE_PASSKEY_DISMISSED_KEY, 'true');
    }
  } catch {
    // Best effort: a storage failure only means the offer may reappear on a later
    // fresh login, which the no-passkey gate still bounds.
  }
}
